/**
 * NOTE on redux:
 *  - `premiumCheck`/`setPremiumCheck` doesn't exist on web (no module
 *    anywhere). On mobile it was a navigation-flow flag that told the app
 *    shell to swap from the "payment" navigator to the "main app" navigator.
 *    Web routing doesn't need an equivalent — `navigate()` calls below go
 *    straight to the next screen.
 *  - `payment.getPaymentInfo()` doesn't exist (payment duck only has
 *    subscribe/checkout per the payment-stub scope). Dropped; PaymentForm
 *    fetches what it needs itself.
 */
import React, { useEffect } from 'react';

import CSSModule from 'react-css-modules';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CouponLogoIcon, UndrawSuccessIcon } from 'components/Icons';
import { actions as customerActions } from 'coupon-common/src/modules/customerProfile';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import { RouteNames } from '../../../constants';
import messages from './messages';
import styles from './SuccessfulSignUp.module.scss';

const SuccessfulSignUp = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const referrer = useSelector(state => state.signup.premiumSponsor);

  useEffect(() => {
    if (referrer) {
      navigate(RouteNames.FREE_PREMIUM);
    }
  }, [referrer, navigate]);

  const goToPayment = () => navigate(RouteNames.PAYMENT_FORM);

  const purchaseFreePlan = async () => {
    await dispatchAsAsync(customerActions.setFreePlanActive('true'), dispatch);
    navigate(RouteNames.HOME);
  };

  if (referrer) {
    return null;
  }

  return (
    <div styleName="page">
      <div styleName="card">
        <div styleName="logo-wrap">
          <CouponLogoIcon width={170} height={81} />
        </div>

        <h1 styleName="title">{intl.formatMessage(messages.premiumUpgrade)}</h1>
        <p styleName="subtitle">{intl.formatMessage(messages.premiumPrice)}</p>

        <div styleName="benefits-card">
          <p styleName="benefit-title">{intl.formatMessage(messages.redeemUnlimitedCoupons)}</p>
          <p styleName="benefit-description">{intl.formatMessage(messages.redeemLimitFreePlan)}</p>
          <p styleName="benefit-title">{intl.formatMessage(messages.redeemPoints)}</p>
          <p styleName="benefit-title">{intl.formatMessage(messages.unlockPremiumCoupons)}</p>
        </div>

        <div styleName="illustration">
          <UndrawSuccessIcon width={217} height={200} />
        </div>

        <div styleName="button-row">
          <button type="button" styleName="not-now-btn" onClick={purchaseFreePlan}>
            {intl.formatMessage(messages.notNow)}
          </button>
          <button type="button" styleName="get-premium-btn" onClick={goToPayment}>
            {intl.formatMessage(messages.getPremium)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSSModule(SuccessfulSignUp, styles, { allowMultiple: true });
